import React, { useState, useEffect, createRef } from 'react'
import MapView, { Callout, Marker } from 'react-native-maps'
import * as Location from 'expo-location'
import * as Permissions from 'expo-permissions'
import { StyleSheet, Text, View, Dimensions } from 'react-native'
import { db } from '../firebase'
import { collection, onSnapshot } from 'firebase/firestore'
import { useRecoilValue } from 'recoil'
import { userState } from '../state/atoms'
import { format } from 'date-fns'
import { Box, Heading } from 'native-base'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import FontAwesome from 'react-native-vector-icons/FontAwesome'
import AnimatedLoader from 'react-native-animated-loader'
import Easing from 'react-native/Libraries/Animated/Easing'
import { Animated } from 'react-native'

const MapScreen = () => {
    const mapView = createRef()
    const [location, setLocation] = useState(null)
    const [errorMsg, setErrorMsg] = useState(null)
    const [mapRegion, setMapRegion] = useState(null)
    const [permissions, setPermissions] = useState(false)
    const [markerData, setMarkerData] = useState([])

    const userData = useRecoilValue(userState)

    const Strong = (props) => <Text style={{ fontWeight: 'bold' }}>{props.children}</Text>

    useEffect(() => {
        getLocationAsync()
    }, [])

    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, 'homes'), (snapshot) => {
            snapshot.docs.forEach((doc) => {
                setMarkerData((currentData) => [...currentData, doc.data()])
            })
        })
        return () => {
            unsubscribe()
        }
    }, [])

    let text = 'Waiting..'
    if (errorMsg) {
        text = errorMsg
    } else if (location) {
        text = JSON.stringify(location)
    }

    const handleMapRegionChange = (mapRegion) => {
        setMapRegion(mapRegion)
    }

    const onMarkerPress = (event) => {
        const { id, coordinate } = event.nativeEvent

        setMapRegion({
            latitude: coordinate.latitude,
            longitude: coordinate.longitude,
            latitudeDelta: 0.01,
            longitudeDelta: 0.01,
        })
        goToPoisitionSelected()
    }

    const goToPoisitionSelected = () => {
        mapView.current.animateToRegion(mapRegion, 2000)
    }

    const getLocationAsync = async () => {
        let { status } = await Permissions.askAsync(Permissions.LOCATION_FOREGROUND)
        if (status !== 'granted') {
            setLocation('Permission to access location was denied')
        } else {
            setPermissions(true)
        }

        let location = await Location.getLastKnownPositionAsync({})
        setLocation(JSON.stringify(location))

        setTimeout(() => {
            setMapRegion({
                latitude: location.coords.latitude,
                longitude: location.coords.longitude,
                latitudeDelta: 0.0522,
                longitudeDelta: 0.0221,
            })
        }, 2000)
    }

    const renderMarkers = () => {
        return markerData.map((home, idx) => {
            const date = home?.date_tagged ? format(new Date(home?.date_tagged?.toDate()), 'MMMM do, yyyy') : 'No date'
            if (userData?.username === home?.tagged_by) {
                return (
                    <Marker
                        key={idx}
                        coordinate={{
                            latitude: home?.location?.lat,
                            longitude: home?.location?.long,
                        }}
                        pinColor={home?.review_status === 'pending' ? '#6c387a' : '#90c734'}
                        onPress={(event) => onMarkerPress(event)}
                    >
                        <Callout tooltip>
                            {home?.review_status === 'approved' && (
                                <View style={styles.pointsCircle}>
                                    <Text style={styles.pointsText}>{home?.points_awarded}</Text>
                                    <Text style={styles.pointsSubText}>Points</Text>
                                </View>
                            )}
                            <View style={home?.review_status === 'pending' ? styles.pendingTooltip : styles.approvedTooltip}>
                                <View style={styles.tooltipHeader}>
                                    <Text style={styles.tooltipTitle}>{home?.review_status === 'pending' ? 'Awaiting Review' : 'You claimed this!'}</Text>
                                    <View style={{ alignSelf: 'flex-end' }}>
                                        {home?.review_status === 'approved' && <FontAwesome name="check-circle" size={24} color="#fff" />}
                                        {home?.review_status === 'pending' && <FontAwesome name="history" size={24} color="#fff" />}
                                        {home?.review_status === 'declined' && <FontAwesome name="times-circle" size={24} color="#fff" />}
                                    </View>
                                </View>
                                <View style={styles.tooltipBody}>
                                    <Text style={styles.tooltipText}>{home?.address}</Text>
                                    <Box style={styles.tooltipLatLongContainer}>
                                        <MaterialCommunityIcons name="map-marker" size={14} color="white" style={{ marginRight: 5 }} />
                                        <Heading style={styles.tooltipLatLong}>
                                            {Math.round(home?.location?.lat * 100000) / 100000}, {Math.round(home?.location?.long * 100000) / 100000}
                                        </Heading>
                                    </Box>
                                </View>
                                <Text style={styles.tooltipSubText}>
                                    Hunted on <Strong>{date}</Strong>
                                </Text>
                            </View>
                        </Callout>
                    </Marker>
                )
            }
        })
    }

    return mapRegion ? (
        <View style={styles.container}>
            <MapView ref={mapView} initialRegion={mapRegion} style={styles.map} onRegionChangeComplete={handleMapRegionChange} showsUserLocation showsPointsOfInterest={false} showsIndoors={false} pitchEnabled={false} tintColor="#6c387a" onMarkerPress={onMarkerPress}>
                {renderMarkers()}
            </MapView>
        </View>
    ) : (
        <AnimatedLoader visible source={require('../assets/animations/map-phone.json')} animationStyle={{ width: 300, height: 300 }} overlayColor="rgba(108, 56, 122, 1)" speed={2} />
    )
}

export default MapScreen

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#fff',
        alignItems: 'center',
        justifyContent: 'center',
    },
    map: {
        width: Dimensions.get('window').width,
        height: Dimensions.get('window').height,
    },
    pointsCircle: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: '#6c387a',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        top: -15,
        left: -15,
        zIndex: 1,
    },
    pointsText: {
        color: '#fff',
        fontWeight: 'bold',
        fontSize: 20,
    },
    pointsSubText: {
        color: '#fff',
        fontSize: 8,
        marginTop: -5,
    },
    pendingTooltip: {
        backgroundColor: '#6c387a',
        padding: 20,
        borderRadius: 10,
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        height: 150,
    },
    approvedTooltip: {
        backgroundColor: '#90c734',
        padding: 20,
        borderRadius: 10,
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        height: 150,
    },
    tooltipHeader: {
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    tooltipBody: {
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    tooltipLatLongContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
    },
    tooltipLatLong: {
        color: 'white',
        fontSize: 14,
        margin: 0,
        textShadow: '1px 1px 1px rgba(0, 0, 0, 0.5)',
    },
    tooltipTitle: {
        color: '#fff',
        fontSize: 20,
    },
    tooltipText: {
        color: '#fff',
    },
    tooltipSubText: {
        color: '#fff',
        fontSize: 12,
    },
})
